import { useState, useEffect, useRef, useCallback } from 'react';
import { Task } from '../types';
import { todayStr } from '../utils/dateUtils';
import { useGanttTimelineGeometry } from './gantt/useGanttTimelineGeometry';
import { useGanttScrollAndZoom } from './gantt/useGanttScrollAndZoom';
import { useGanttPointerDragEffect, GanttActiveDrag } from './gantt/useGanttPointerDragEffect';

export const useGanttState = (
  visibleTasks: Task[],
  tasks: Task[],
  commitTasks: (tasks: Task[]) => void,
  showDependencies: boolean = true,
  datePadding: number = 0,
  hideEmptyDays: boolean = false,
  viewMode: string = 'gantt'
) => {
  const isGanttActive = viewMode === 'gantt';
  const [dayWidth, setDayWidth] = useState(28);
  const [isFitMode, setIsFitMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [activeDrag, setActiveDrag] = useState<GanttActiveDrag | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragOverId, setDragOverId] = useState<string | number | null>(null);
  const [linkDrag, setLinkDrag] = useState<{ sourceId: number | string; mouseX: number; mouseY: number } | null>(null);
  const [linkTargetId, setLinkTargetId] = useState<string | number | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | string | null>(null);

  const [containerHeight] = useState(600);
  const [containerWidth] = useState(1200);
  const [listScrollTop] = useState(0);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sidebarListRef = useRef<{ element?: HTMLDivElement | null; [key: string]: unknown } | null>(null);
  const ganttListRef = useRef<{ element?: HTMLDivElement | null; [key: string]: unknown } | null>(null);
  const scrollSourceRef = useRef<'sidebar' | 'gantt' | null>(null);
  const scrollSyncTimerRef = useRef<number | null>(null);
  const listScrollTopRef = useRef(0);
  const lastRenderScrollTopRef = useRef(0);
  const activeTaskElementRef = useRef<HTMLDivElement | null>(null);
  const panStartXRef = useRef(0);
  const panStartYRef = useRef(0);
  const panStartScrollLeftRef = useRef(0);
  const panStartScrollTopRef = useRef(0);

  const isPanningRef = useRef(isPanning);
  const activeDragRef = useRef<GanttActiveDrag | null>(activeDrag);
  const linkDragRef = useRef<{ sourceId: number | string; mouseX: number; mouseY: number } | null>(linkDrag);
  const linkTargetIdRef = useRef<string | number | null>(linkTargetId);
  const tasksRef = useRef(tasks);
  const visibleTasksRef = useRef(visibleTasks);
  const commitTasksRef = useRef(commitTasks);
  const dayWidthRef = useRef(dayWidth);
  const timelineDatesRef = useRef<Date[]>([]);
  const dateToIndexMapRef = useRef<Record<string, number>>({});
  const dragOffsetRef = useRef(dragOffset);
  const editTaskCallbackRef = useRef<((id: string | number) => void) | null>(null);

  useEffect(() => { isPanningRef.current = isPanning; }, [isPanning]);
  useEffect(() => { activeDragRef.current = activeDrag; }, [activeDrag]);
  useEffect(() => { linkDragRef.current = linkDrag; }, [linkDrag]);
  useEffect(() => { linkTargetIdRef.current = linkTargetId; }, [linkTargetId]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);
  useEffect(() => { visibleTasksRef.current = visibleTasks; }, [visibleTasks]);
  useEffect(() => { commitTasksRef.current = commitTasks; }, [commitTasks]);
  useEffect(() => { dayWidthRef.current = dayWidth; }, [dayWidth]);
  useEffect(() => { dragOffsetRef.current = dragOffset; }, [dragOffset]);
  useEffect(() => { listScrollTopRef.current = listScrollTop; }, [listScrollTop]);

  const {
    leftPadding,
    rightPadding,
    gridStart,
    gridEnd,
    timelineDates,
    dateToIndexMap,
    headerDates,
    months,
    taskIdMap,
    fullTaskMap,
    getTaskX,
    getTaskWidth,
    getDependencyPath,
  } = useGanttTimelineGeometry({
    isGanttActive,
    visibleTasks,
    tasks,
    datePadding,
    hideEmptyDays,
    dayWidth,
    timelineDatesRef,
    dateToIndexMapRef,
  });

  const {
    clearScrollSource,
    handleSidebarScroll,
    handleGanttScroll,
    handleWheel,
    scrollToInitialView,
    toggleZoomToFit,
    scrollToToday,
    zoomIn,
    zoomOut,
    scrollByDays,
    scrollToTask,
  } = useGanttScrollAndZoom({
    visibleTasks,
    visibleTasksRef,
    gridStart,
    dateToIndexMap,
    dateToIndexMapRef,
    dayWidth,
    dayWidthRef,
    setDayWidth,
    isFitMode,
    setIsFitMode,
    selectedTaskId,
    setSelectedTaskId,
    scrollContainerRef,
    sidebarListRef,
    ganttListRef,
    scrollSourceRef,
    scrollSyncTimerRef,
    listScrollTopRef,
    lastRenderScrollTopRef,
  });

  const onContainerRef = useCallback((_node: HTMLDivElement | null) => {}, []);

  const handleDragStart = useCallback((e: React.MouseEvent, id: number | string, action: 'move' | 'left' | 'right') => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;

    e.stopPropagation();
    const element = e.currentTarget.closest('.task-bar-container') as HTMLDivElement;
    activeTaskElementRef.current = element;
    dragOffsetRef.current = 0;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const scrollLeft = scrollContainer.scrollLeft;

    const effectiveStart = task.start || task.end || todayStr;
    const effectiveEnd = task.end || task.start || todayStr;

    const startIdx = dateToIndexMapRef.current[effectiveStart];
    const barStartPos = startIdx !== undefined ? startIdx * dayWidthRef.current : 0;
    const mouseXInGrid = (e.clientX - containerRect.left) + scrollLeft;
    const anchorOffset = mouseXInGrid - barStartPos;

    setActiveDrag({
      id,
      action,
      startClientX: e.clientX,
      startScrollLeft: scrollLeft,
      anchorOffset,
      hasMoved: false,
      initialStart: effectiveStart,
      initialEnd: effectiveEnd
    });
  }, [tasks]);

  const handleGridMouseDownNative = useCallback((e: MouseEvent) => {
    if (e.button !== 0 || e.ctrlKey) return;

    const target = e.target as HTMLElement;
    if (target.closest('.task-bar-container') || target.closest('button') || target.closest('.dependency-port')) {
      return;
    }

    e.preventDefault();

    setIsPanning(true);
    panStartXRef.current = e.clientX;
    panStartYRef.current = e.clientY;
    panStartScrollLeftRef.current = scrollContainerRef.current?.scrollLeft || 0;
    panStartScrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
  }, []);

  const handleLinkStart = useCallback((e: React.MouseEvent, sourceId: number | string) => {
    e.stopPropagation();
    e.preventDefault();
    const containerRect = scrollContainerRef.current?.getBoundingClientRect();
    const scrollX = scrollContainerRef.current?.scrollLeft ?? 0;
    const scrollY = listScrollTopRef.current || 0;
    const mouseX = containerRect ? (e.clientX - containerRect.left + scrollX) : e.clientX;
    const mouseY = containerRect ? (e.clientY - containerRect.top + scrollY) : e.clientY;

    if (!isNaN(mouseX) && !isNaN(mouseY)) {
      setLinkDrag({ sourceId: sourceId.toString(), mouseX, mouseY });
    }
  }, []);

  useGanttPointerDragEffect({
    activeDrag,
    setActiveDrag,
    linkDrag,
    setLinkDrag,
    isPanning,
    setIsPanning,
    setLinkTargetId,
    setDragOffset,
    activeDragRef,
    isPanningRef,
    linkDragRef,
    linkTargetIdRef,
    tasksRef,
    visibleTasksRef,
    commitTasksRef,
    dayWidthRef,
    timelineDatesRef,
    dateToIndexMapRef,
    dragOffsetRef,
    editTaskCallbackRef,
    scrollContainerRef,
    sidebarListRef,
    scrollSourceRef,
    listScrollTopRef,
    activeTaskElementRef,
    panStartXRef,
    panStartYRef,
    panStartScrollLeftRef,
    panStartScrollTopRef,
    clearScrollSource,
  });

  return {
    dayWidth, setDayWidth,
    leftPadding,
    rightPadding,
    isPanning, setIsPanning,
    activeDrag, setActiveDrag,
    dragOffset, setDragOffset,
    dragOverId, setDragOverId,
    showDependencies,
    linkDrag, setLinkDrag,
    linkTargetId, setLinkTargetId,
    containerHeight, containerWidth,
    listScrollTop,
    scrollContainerRef,
    timelineRef: scrollContainerRef,
    sidebarListRef,
    ganttListRef,
    onContainerRef,
    handleSidebarScroll,
    handleGanttScroll,
    handleWheel,
    zoomToFit: toggleZoomToFit,
    toggleZoomToFit,
    isFitMode,
    scrollByDays,
    handleLinkStart,
    gridStart, gridEnd,
    timelineDates,
    dateToIndexMap,
    headerDates,
    months,
    listScrollTopRef,
    lastRenderScrollTopRef,
    scrollToToday,
    scrollToInitialView,
    zoomIn,
    zoomOut,
    handleGridMouseDown: handleGridMouseDownNative,
    handleDragStart,
    getTaskX,
    getTaskWidth,
    getDependencyPath,
    taskIdMap,
    fullTaskMap,
    timelineDatesRef,
    dateToIndexMapRef,
    selectedTaskId,
    scrollToTask,
    editTaskCallbackRef
  };
};
